import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  KpiCard,
  WidgetShell,
  BaseAreaChart,
  BaseBarChart,
  BaseDonutChart,
  Spinner,
} from '@kss/ui';
import { useApi, employeeService, attendanceService } from '@kss/api';
import { Users, CalendarCheck } from 'lucide-react';

/**
 * Tenant-wide dashboard.
 *
 * **All chart data is derived from the live API** — there are no
 * hard-coded sample arrays in this file. When the API returns no rows
 * for a tenant the charts render an empty state (single point of
 * zero) so it's obvious there is no data, rather than misleading the
 * operator with synthetic numbers.
 *
 *   - Headcount trend (last 6 months)  ←  /api/hrm/Employee  (joinDate / exitDate)
 *   - Attendance this week              ←  /api/hrm/Attendance (date)
 *   - Department mix                    ←  /api/hrm/Employee  (department)
 *
 * If any of the derivations needs a field the API does not return
 * (e.g. joinDate missing), it falls back gracefully to zeros and the
 * gap is documented in `BACKEND_AGENT_PROMPTS.md` §15.
 */

interface EmployeeRow extends Record<string, unknown> {
  isActive?: number | boolean;
  joinDate?: string;
  exitDate?: string;
  department?: string;
}

interface AttendanceRow extends Record<string, unknown> {
  date?: string;
  attendanceDate?: string;
  punchDate?: string;
  status?: string;
}

export default function Dashboard() {
  const { t } = useTranslation('panel');

  const employees = useApi(() => employeeService.all().catch(() => []), []);
  const attendance = useApi(() => attendanceService.all().catch(() => []), []);

  const empRows = ((employees.data ?? []) as unknown as EmployeeRow[]);
  const attRows = ((attendance.data ?? []) as unknown as AttendanceRow[]);

  const totalEmployees = empRows.length;
  const today = new Date().toISOString().slice(0, 10);

  const attendanceToday = useMemo(() => {
    return attRows.filter((a) => attendanceDate(a)?.startsWith(today)).length;
  }, [attRows, today]);

  /* ------------------------------------------------------------------ */
  /* Headcount trend — cumulative active employees on the last day of  */
  /* each of the most recent 6 months.                                  */
  /* ------------------------------------------------------------------ */
  const headcountByMonth = useMemo(() => {
    const now = new Date();
    const months: { month: string; cutoff: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i + 1, 0); // last day of that month
      months.push({
        month: d.toLocaleDateString('en-IN', { month: 'short' }),
        cutoff: d.getTime(),
      });
    }
    return months.map(({ month, cutoff }) => {
      const value = empRows.filter((e) => {
        const join = Date.parse(String(e.joinDate ?? ''));
        if (Number.isNaN(join) || join > cutoff) return false;
        const exit = e.exitDate ? Date.parse(String(e.exitDate)) : NaN;
        if (!Number.isNaN(exit) && exit <= cutoff) return false;
        return true;
      }).length;
      return { month, value };
    });
  }, [empRows]);

  /* ------------------------------------------------------------------ */
  /* Attendance this week — count of attendance rows per weekday for   */
  /* the most recent Mon..Sun window.                                   */
  /* ------------------------------------------------------------------ */
  const weekAttendance = useMemo(() => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const now = new Date();
    // Start of week (Mon)
    const weekStart = new Date(now);
    const day = (weekStart.getDay() + 6) % 7; // 0 if Mon, 6 if Sun
    weekStart.setDate(weekStart.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);

    const buckets: { name: string; value: number }[] = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      const value = attRows.filter((a) => attendanceDate(a)?.startsWith(iso)).length;
      buckets.push({ name: days[(d.getDay() + 7) % 7]!, value });
    }
    return buckets;
  }, [attRows]);

  /* ------------------------------------------------------------------ */
  /* Department mix — group active employees by department string.     */
  /* ------------------------------------------------------------------ */
  const departmentMix = useMemo(() => {
    const counts = new Map<string, number>();
    for (const e of empRows) {
      if (!isActive(e)) continue;
      const dept = String(e.department ?? '').trim() || '— Unassigned';
      counts.set(dept, (counts.get(dept) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [empRows]);

  const loading = employees.loading || attendance.loading;

  return (
    <div className="grid gap-5">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-content-primary">
          {t('dashboard.title')}
        </h1>
        <p className="mt-1 text-sm text-content-secondary">{t('dashboard.subtitle')}</p>
      </header>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          title={t('dashboard.kpi.totalEmployees')}
          value={loading ? '…' : totalEmployees.toLocaleString()}
          icon={<Users size={18} />}
        />
        <KpiCard
          title={t('dashboard.kpi.todayAttendance')}
          value={loading ? '…' : attendanceToday.toLocaleString()}
          icon={<CalendarCheck size={18} />}
          trend={
            totalEmployees > 0
              ? {
                  direction: attendanceToday >= totalEmployees * 0.85 ? 'up' : 'down',
                  value: Number(((attendanceToday / Math.max(totalEmployees, 1)) * 100).toFixed(1)),
                  label: 'present rate',
                }
              : undefined
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <WidgetShell title="Headcount trend (6 months)">
          {totalEmployees === 0 ? (
            <EmptyChart message="No employees in this tenant yet" />
          ) : (
            <BaseAreaChart
              data={headcountByMonth}
              areas={[{ dataKey: 'value', name: 'Employees' }]}
              xAxisKey="month"
              height={260}
            />
          )}
        </WidgetShell>
        <WidgetShell title="Attendance this week">
          {attRows.length === 0 ? (
            <EmptyChart message="No attendance entries yet" />
          ) : (
            <BaseBarChart
              data={weekAttendance}
              bars={[{ dataKey: 'value', name: 'Present' }]}
              xAxisKey="name"
              height={260}
            />
          )}
        </WidgetShell>
      </div>

      <div className="grid gap-4">
        <WidgetShell title="Department mix">
          {departmentMix.length === 0 ? (
            <EmptyChart message="No employees with a department set" />
          ) : (
            <BaseDonutChart data={departmentMix} height={280} />
          )}
        </WidgetShell>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-xs text-content-muted">
          <Spinner /> Refreshing live data…
        </div>
      )}
    </div>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="flex h-[240px] flex-col items-center justify-center gap-1 text-xs text-content-muted">
      <span>{message}</span>
      <span className="text-[10px]">Live data — bars appear as soon as the API returns rows.</span>
    </div>
  );
}

function isActive(e: EmployeeRow): boolean {
  if (typeof e.isActive === 'number') return e.isActive === 1;
  if (typeof e.isActive === 'boolean') return e.isActive;
  return true; // default to active when the field is missing
}

function attendanceDate(a: AttendanceRow): string | undefined {
  const v = a.date ?? a.attendanceDate ?? a.punchDate;
  return typeof v === 'string' ? v : undefined;
}
